import type { Uuid } from "@planerius/contracts"
import type {
  ClientCardView,
  ClientConsentsResponse,
  ClientListResult,
  CreateClientRequest,
  ExportClientsRequest,
  ExportJobView,
  GrantClientConsentRequest,
  RevealClientPhoneRequest,
  RevealClientPhoneResult,
  RevokeClientConsentRequest,
} from "@planerius/contracts/cabinet"

import { apiRequest } from "./http"
import { newIdempotencyKey } from "./idempotency"

/**
 * Клиенты салона (3-А1, 3-А2).
 *
 * ГЛАВНОЕ ПРО ПЕРСОНАЛЬНЫЕ ДАННЫЕ, И ЭТО НЕ ПРАВИЛО ЭКРАНА, А ПРАВИЛО ЭТОГО СЛОЯ.
 * Телефон гасит СЕРВЕР: в ответе приходит `phone: null` и остаётся `phoneMasked`.
 * Поэтому кабинет НИКОГДА не решает сам, показывать номер или нет, и не считает права
 * заново — он показывает то, что приехало. Обратное («приехал номер, но роль мастер,
 * скроем») выглядит как защита, а на деле номер уже в браузере, уже в отладчике сети
 * и уже в кеше: скрыт он только от глаз честного человека.
 *
 * Открытие номера целиком — ДЕЙСТВИЕ, а не поле: POST reveal-phone пишется в аудит,
 * поэтому у него отдельный вызов и отдельный ключ идемпотентности.
 */

/** Что спрашиваем у списка. Повторяет ClientListQuery, но без полей, которых нет у экрана. */
export interface ClientsQuery {
  readonly search?: string | undefined
  readonly cursor?: string | undefined
  readonly limit?: number | undefined
  readonly tagIds?: readonly Uuid[] | undefined
  readonly blacklistedOnly?: boolean | undefined
  readonly minNoShowCount?: number | undefined
  /** «Не были больше 3 месяцев» — календарная дата, а не «90 дней»: считает её экран. */
  readonly lastVisitBefore?: string | undefined
  readonly lastVisitEmployeeId?: Uuid | undefined
  readonly sort?: "last_visit" | "name" | "visits" | undefined
}

export function fetchClients(
  query: ClientsQuery,
  signal?: AbortSignal,
): Promise<ClientListResult> {
  return apiRequest<ClientListResult>("/cabinet/clients", {
    query: {
      search: query.search,
      cursor: query.cursor,
      limit: query.limit,
      tagIds: query.tagIds,
      blacklistedOnly: query.blacklistedOnly,
      minNoShowCount: query.minNoShowCount,
      lastVisitBefore: query.lastVisitBefore,
      lastVisitEmployeeId: query.lastVisitEmployeeId,
      sort: query.sort,
    },
    signal,
  })
}

export function fetchClientCard(clientId: string, signal?: AbortSignal): Promise<ClientCardView> {
  return apiRequest<ClientCardView>(`/cabinet/clients/${clientId}`, { signal })
}

/**
 * Строка истории визитов. Своего типа в контрактах нет — форма собрана сервисом
 * (apps/api/src/modules/cabinet/clients.service.ts, метод visits), поэтому описана здесь
 * и обязана меняться вместе с ним.
 *
 * priceMinor допускает null: деньги гасит сервер по праву finance.view и по настройке
 * «деньги видны мастеру». Это не «ошибка загрузки» и не ноль рублей.
 */
export interface ClientVisitRow {
  readonly id: string
  readonly branchId: string
  readonly employeeId: string | null
  readonly employeeName: string | null
  readonly status: string
  readonly startAt: string
  readonly endAt: string
  readonly priceMinor: number | null
  readonly comment: string | null
  readonly serviceNames: readonly string[]
}

export function fetchClientVisits(
  clientId: string,
  limit = 50,
  signal?: AbortSignal,
): Promise<ClientVisitRow[]> {
  return apiRequest<ClientVisitRow[]>(`/cabinet/clients/${clientId}/visits`, {
    query: { limit },
    signal,
  })
}

export function createClient(request: CreateClientRequest): Promise<{ readonly id: string }> {
  return apiRequest<{ id: string }>("/cabinet/clients", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("client-create"),
  })
}

/**
 * Показать номер целиком.
 *
 * Ключ идемпотентности здесь не про дубли строк, а про дубли ЗАПИСЕЙ В АУДИТЕ: повтор
 * после обрыва не должен выглядеть как второй просмотр номера сотрудником.
 */
export function revealClientPhone(
  request: RevealClientPhoneRequest,
): Promise<RevealClientPhoneResult> {
  return apiRequest<RevealClientPhoneResult>("/cabinet/clients/reveal-phone", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("client-reveal"),
  })
}

/**
 * Выгрузка выборки — ЗАЯВКА, а не файл (ТЗ §3.5).
 *
 * Ответ — задание (ExportJobView), а не ссылка на скачивание: выгрузка всегда
 * асинхронная, ссылка живёт ограниченное время, а перед запуском сервер требует
 * свежий второй фактор. Поэтому функция называется «запросить», а не «скачать».
 */
export function requestClientsExport(request: ExportClientsRequest): Promise<ExportJobView> {
  return apiRequest<ExportJobView>("/cabinet/clients/export", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("clients-export"),
  })
}

/**
 * Формируются ли файлы выгрузки ВООБЩЕ.
 *
 * Нужно ДО нажатия кнопки: экран ограниченного доступа обещал выгрузку словами и рисовал
 * кнопку, а обработчик выгрузок не подключён ни в одном окружении — каждое нажатие
 * отвечало 503. Обещание, которого мы не держим, человек читает как «базу не отдают
 * за неоплату».
 */
export function clientsExportAvailable(signal?: AbortSignal): Promise<{ readonly available: boolean }> {
  return apiRequest("/cabinet/clients/export-availability", { signal })
}

/**
 * Согласие, полученное на бумаге или устно (3-А5). Источник не передаётся вовсе: сервер
 * ставит «администратор» и автора по факту вызова, и нарисовать «клиент нажал на сайте»
 * из кабинета нельзя.
 */
export function grantClientConsent(request: GrantClientConsentRequest): Promise<ClientConsentsResponse> {
  return apiRequest<ClientConsentsResponse>("/cabinet/clients/consents", { method: "POST", body: request })
}

/** Отзыв по требованию клиента: строка журнала остаётся, отзыв действует немедленно. */
export function revokeClientConsent(request: RevokeClientConsentRequest): Promise<ClientConsentsResponse> {
  return apiRequest<ClientConsentsResponse>("/cabinet/clients/consents/revoke", { method: "POST", body: request })
}
